import PropTypes from 'prop-types';
import { compileRuleHeadline } from '../xallof/ruleNarrative';

const EM_DASH = '—';
const SUMMARY_FALLBACK = '(rule summary unavailable)';

// Render rules as a plain-English narrative ("If currency equals EUR then
// vatNumber is required") rather than the terse IF/THEN field list, so the
// Conditional Rules diff reads clearly for non-technical reviewers.
// compileRuleHeadline must never throw — fall back to a stable string instead.
function safeSummary(rule) {
	try {
		return compileRuleHeadline(rule);
	} catch (e) {
		return SUMMARY_FALLBACK;
	}
}

function CountPill({ status, glyph, count }) {
	if (!count) return null;
	return (
		<span className={`dsv-compare__pill dsv-compare__pill--${status}`}>{glyph}{count}</span>
	);
}

CountPill.propTypes = {
	status: PropTypes.string,
	glyph: PropTypes.string,
	count: PropTypes.number,
};

function RuleCard({ status, glyph, gutter, number, before, after, beforeNone, afterNone, index }) {
	const beforeBox = beforeNone ? '--none' : '--old';
	const afterBox = afterNone ? '--none' : '--new';
	return (
		<div className="dsv-compare__rulecard" data-testid={`dsv-compare-rule-${status}-${index}`}>
			<div className="dsv-compare__rulecard-sum">
				<span className={`dsv-compare__gutter dsv-compare__gutter--${gutter}`}>{glyph}</span>
				{` Rule #${number} ${status}`}
			</div>
			<div className="dsv-compare__rba">
				<div>
					<div className="dsv-compare__rba-lbl">Before</div>
					<div className={`dsv-compare__rba-box dsv-compare__rba-box${beforeBox}`}>{before}</div>
				</div>
				<div>
					<div className="dsv-compare__rba-lbl">After</div>
					<div className={`dsv-compare__rba-box dsv-compare__rba-box${afterBox}`}>{after}</div>
				</div>
			</div>
		</div>
	);
}

RuleCard.propTypes = {
	status: PropTypes.string,
	glyph: PropTypes.string,
	gutter: PropTypes.string,
	number: PropTypes.number,
	index: PropTypes.number,
	before: PropTypes.node,
	after: PropTypes.node,
	beforeNone: PropTypes.bool,
	afterNone: PropTypes.bool,
};

function RuleDiffSection({ rules, title }) {
	const added = rules?.added || [];
	const removed = rules?.removed || [];
	const modified = rules?.modified || [];
	if (added.length === 0 && removed.length === 0 && modified.length === 0) return null;

	// Globally numbered in order: modified, then added, then removed.
	let n = 0;
	const cards = [];

	modified.forEach((m, i) => {
		n += 1;
		cards.push(
			<RuleCard
				key={`m-${i}`}
				status="modified"
				glyph="~"
				gutter="m"
				number={n}
				index={i}
				before={safeSummary(m.baseRule)}
				after={safeSummary(m.rule)}
			/>,
		);
	});

	added.forEach((r, i) => {
		n += 1;
		cards.push(
			<RuleCard
				key={`a-${i}`}
				status="added"
				glyph="+"
				gutter="a"
				number={n}
				index={i}
				before={EM_DASH}
				beforeNone
				after={safeSummary(r)}
			/>,
		);
	});

	removed.forEach((r, i) => {
		n += 1;
		cards.push(
			<RuleCard
				key={`r-${i}`}
				status="removed"
				glyph={'−'}
				gutter="r"
				number={n}
				index={i}
				before={safeSummary(r)}
				after={EM_DASH}
				afterNone
			/>,
		);
	});

	return (
		<div className="dsv-compare__sheet" data-testid={`dsv-compare-section-${title}`}>
			<div className="dsv-compare__sheet-title">
				{title}
				<span className="dsv-compare__sheet-title-counts">
					<CountPill status="modified" glyph="~" count={modified.length} />
					<CountPill status="added" glyph="+" count={added.length} />
					<CountPill status="removed" glyph={'−'} count={removed.length} />
				</span>
			</div>
			{cards}
		</div>
	);
}

RuleDiffSection.propTypes = {
	rules: PropTypes.shape({
		added: PropTypes.array,
		removed: PropTypes.array,
		modified: PropTypes.array,
	}),
	title: PropTypes.string,
};

RuleDiffSection.defaultProps = {
	title: 'Conditional Rules',
};

export default RuleDiffSection;
